import { useEffect, useState } from 'react';
import type { ToolCallMessagePartComponent } from '@assistant-ui/react';
import { Loader2Icon } from 'lucide-react';
import { ChatMarkdown } from '@/components/assistant-ui/markdown-text';
import { stripFollowups } from '@/components/assistant-ui/followup-markers';
import { useCentrifugeProgress } from '@/hooks/useCentrifugeProgress';
import { useGetMcpBackgroundToolQuery } from '../../api/mcpApi';
import {
  PresentChartCard,
  type PresentChartArgs
} from '../ChartToolUI';
import { publishBackgroundReport } from './backgroundReportStore';

type BackgroundResult = {
  background?: boolean;
  channelId?: string;
  message?: string;
  isDone?: boolean;
  failed?: boolean;
  summary?: string;
  chart?: PresentChartArgs | null;
};

function isChart(value: unknown): value is PresentChartArgs {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const row = value as PresentChartArgs;
  return Array.isArray(row.data) && typeof row.xKey === 'string';
}

function parseResult(result: unknown): BackgroundResult | null {
  if (!result) return null;
  if (typeof result === 'object' && !Array.isArray(result)) {
    return result as BackgroundResult;
  }
  if (typeof result !== 'string' || !result.trim()) return null;
  const start = result.indexOf('{');
  const end = result.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  try {
    return JSON.parse(result.slice(start, end + 1)) as BackgroundResult;
  } catch {
    return null;
  }
}

/** Live progress for a tool that finishes after the chat stream closes. */
export const BackgroundToolUI: ToolCallMessagePartComponent = ({ result }) => {
  const stored = parseResult(result);
  const channelId = stored?.channelId?.trim() ?? '';
  const background = stored?.background === true && Boolean(channelId);
  const storedDone = stored?.isDone === true;
  const [sawConnection, setSawConnection] = useState(false);

  const { data, refetch } = useGetMcpBackgroundToolQuery(channelId, {
    skip: !background || storedDone
  });
  const pollDone = storedDone || data?.isDone === true;
  const { connected, progressData } = useCentrifugeProgress({
    channelId,
    active: background && !pollDone,
    onComplete: () => {
      void refetch();
    }
  });
  const done =
    pollDone ||
    progressData?.isDone === true ||
    progressData?.finished === true;
  const failed =
    stored?.failed === true ||
    data?.failed === true ||
    progressData?.executionStatus === 'failed';
  const summary =
    (typeof data?.summary === 'string' && data.summary.trim()) ||
    stored?.summary?.trim() ||
    '';
  const liveSummary =
    (typeof progressData?.summary === 'string' && progressData.summary.trim()) ||
    summary;
  const chart =
    (isChart(progressData?.chart) && progressData.chart) ||
    (isChart(data?.chart) && data.chart) ||
    (isChart(stored?.chart) && stored.chart) ||
    null;

  useEffect(() => {
    if (!channelId || !liveSummary) return;
    publishBackgroundReport(channelId, { summary: liveSummary, chart });
  }, [channelId, chart, liveSummary]);

  useEffect(() => {
    if (connected) setSawConnection(true);
  }, [connected]);

  useEffect(() => {
    if (!background || pollDone) return;
    const timer = window.setInterval(() => {
      void refetch();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [background, pollDone, refetch]);

  if (!background) return null;

  if (storedDone) return null;

  if (done && liveSummary && !failed) {
    return (
      <div dir="rtl" className="flex w-full flex-col gap-3">
        {chart ? <PresentChartCard args={chart} /> : null}
        <ChatMarkdown text={stripFollowups(liveSummary)} />
      </div>
    );
  }

  const liveMessage =
    (typeof progressData?.message === 'string' && progressData.message.trim()) ||
    data?.message ||
    stored?.message ||
    'گزارش سرعت در حال ساخت است.';
  const percent =
    typeof progressData?.progress_percentage === 'number'
      ? Math.min(100, Math.max(0, Math.round(progressData.progress_percentage)))
      : typeof data?.percent === 'number'
        ? data.percent
        : null;

  return (
    <div
      dir="rtl"
      className="border-border/70 bg-[var(--card)] text-foreground flex w-full flex-col gap-2.5 rounded-xl border px-3.5 py-3"
    >
      <div className="flex min-w-0 items-center gap-2">
        {!done ? (
          <Loader2Icon
            className="text-primary size-4 shrink-0 animate-spin"
            aria-hidden
          />
        ) : null}
        <span className="text-foreground text-sm leading-6 font-semibold">
          {done && failed ? liveMessage : done ? 'گزارش سرعت آماده است.' : liveMessage}
        </span>
      </div>
      {!done ? (
        <div
          className="bg-primary/15 relative h-1 overflow-hidden rounded-full"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent ?? undefined}
          aria-label="پیشرفت گزارش سرعت"
          style={{ direction: 'ltr' }}
        >
          <span
            className={
              percent == null
                ? 'bg-primary mcp-skill-run__bar--indeterminate block h-full rounded-full'
                : 'bg-primary block h-full rounded-full transition-[width] duration-300'
            }
            style={percent == null ? undefined : { width: `${percent}%` }}
          />
        </div>
      ) : null}
      {!done && sawConnection && !connected ? (
        <p className="text-muted-foreground mb-0 text-xs leading-6">
          نمایش زنده قطع شد؛ ساخت گزارش ادامه دارد.
        </p>
      ) : null}
    </div>
  );
};
